/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import { SmallButton } from '../../../../../styles/ui/Button';
import StatusBadge from '../../components/StatusBadge';
import {
   ADMISSION_DOCUMENT_STATUS,
   ADMISSION_DOCUMENT_STATUS_LABEL,
   COLLECTION_KIND,
   isMandatoryAdmissionDoc,
} from '../../constants';
import {
   UPLOAD_ACCEPTED_MIME_TYPES,
   validateUploadFile,
} from '../../utils/resume';
import { DocRow, DocRowMain, DocRowDetail } from '../styles';

// ---------------------------------------------------------------------------
// Uma linha do checklist de admissão. Ações por collection_kind: FILE = upload
// inline (input escondido — SEM modal, fc7 cap.2 H.2.2) + download presigned;
// VALUE = valor textual no detalhe expandido (SÓ o valor — o campo de
// observações foi removido em 2026-07-15; o backend removeu `notes` junto).
// Documentos MÍNIMOS OBRIGATÓRIOS (isMandatoryAdmissionDoc) não expõem
// "Não se aplica"; o "Reativar" continua disponível pra corrigir estado legado.
// O PATCH/upload em si é do pai (busyDocumentId único contra duplo-submit);
// esta linha é apresentação + coleta de input.
// ---------------------------------------------------------------------------

const STATUS_VARIANT = {
   PENDENTE: 'neutral',
   RECEBIDO: 'green',
   ENVIADO_CONTABILIDADE: 'blue',
   NAO_APLICA: 'amber',
};

export default function DocumentRow({
   document: doc,
   canAct,
   isBusy,
   onUploadFile,
   onSaveDetail,
   onSetStatus,
   onDownload,
}) {
   const fileInputRef = useRef(null);
   const [isExpanded, setIsExpanded] = useState(false);
   const [valueDraft, setValueDraft] = useState(doc.value ?? '');

   // Re-sincroniza o rascunho quando o documento é recarregado.
   useEffect(() => {
      setValueDraft(doc.value ?? '');
   }, [doc.value]);

   const isFile = doc.document_type?.collection_kind === COLLECTION_KIND.FILE;
   const isValue = doc.document_type?.collection_kind === COLLECTION_KIND.VALUE;
   const isNaoAplica = doc.status === ADMISSION_DOCUMENT_STATUS.NAO_APLICA;
   const isMandatory = isMandatoryAdmissionDoc(doc.document_type);
   // Reparo de linha legada: valor salvo antes do fix de status ficou
   // PENDENTE — o Salvar fica habilitado pra corrigir mesmo sem edição.
   const needsStatusRepair =
      Boolean(doc.value) && doc.status === ADMISSION_DOCUMENT_STATUS.PENDENTE;
   const isDetailDirty =
      valueDraft !== (doc.value ?? '') || needsStatusRepair;

   const handleFileChange = (event) => {
      const file = event.target.files?.[0] ?? null;
      // Permite re-selecionar o mesmo arquivo depois de um erro.
      event.target.value = '';
      if (!file) return;
      const validationError = validateUploadFile(file);
      if (validationError) {
         toast.error(validationError);
         return;
      }
      onUploadFile(doc, file);
   };

   const handleSaveDetail = () => {
      if (!isDetailDirty) return;
      const patch = { value: valueDraft || null };
      // Espelho do comportamento do upload FILE (que seta RECEBIDO no
      // backend): valor preenchido = documento coletado; valor limpo volta a
      // pendente. Mesmo PATCH (atômico). ENVIADO_CONTABILIDADE não é tocado —
      // esse status o operador gere explicitamente.
      if (valueDraft && doc.status === ADMISSION_DOCUMENT_STATUS.PENDENTE) {
         patch.status = ADMISSION_DOCUMENT_STATUS.RECEBIDO;
      } else if (
         !valueDraft &&
         doc.status === ADMISSION_DOCUMENT_STATUS.RECEBIDO
      ) {
         patch.status = ADMISSION_DOCUMENT_STATUS.PENDENTE;
      }
      onSaveDetail(doc, patch);
   };

   return (
      <DocRow $inactive={isNaoAplica}>
         <DocRowMain>
            <div className="doc-name">
               {doc.document_type?.name ?? doc.document_type?.code ?? 'Documento'}
               <StatusBadge variant={STATUS_VARIANT[doc.status] ?? 'neutral'}>
                  {ADMISSION_DOCUMENT_STATUS_LABEL[doc.status] ?? doc.status}
               </StatusBadge>
               {doc.has_file && doc.file_original_name && (
                  <span className="doc-file">{doc.file_original_name}</span>
               )}
               {isValue && doc.value && !isExpanded && (
                  <span className="doc-file">Valor: {doc.value}</span>
               )}
            </div>

            <div className="doc-actions">
               {canAct && isFile && !isNaoAplica && (
                  <>
                     <input
                        ref={fileInputRef}
                        type="file"
                        accept={UPLOAD_ACCEPTED_MIME_TYPES}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                     />
                     <SmallButton
                        type="button"
                        $intent="write"
                        disabled={isBusy}
                        onClick={() => fileInputRef.current?.click()}
                     >
                        {doc.has_file ? 'Substituir arquivo' : 'Enviar arquivo'}
                     </SmallButton>
                  </>
               )}
               {doc.has_file && (
                  <SmallButton
                     type="button"
                     disabled={isBusy}
                     onClick={() => onDownload(doc)}
                  >
                     Baixar
                  </SmallButton>
               )}
               {canAct &&
                  !isNaoAplica &&
                  doc.status === ADMISSION_DOCUMENT_STATUS.RECEBIDO && (
                     <SmallButton
                        type="button"
                        disabled={isBusy}
                        onClick={() =>
                           onSetStatus(
                              doc,
                              ADMISSION_DOCUMENT_STATUS.ENVIADO_CONTABILIDADE,
                           )
                        }
                     >
                        Marcar enviado à contab.
                     </SmallButton>
                  )}
               {isValue && !isNaoAplica && (
                  <SmallButton
                     type="button"
                     disabled={isBusy}
                     onClick={() => setIsExpanded((prev) => !prev)}
                  >
                     {isExpanded ? 'Fechar' : 'Informar valor'}
                  </SmallButton>
               )}
               {canAct && !isMandatory && !isNaoAplica && (
                  <SmallButton
                     type="button"
                     disabled={isBusy}
                     onClick={() =>
                        onSetStatus(doc, ADMISSION_DOCUMENT_STATUS.NAO_APLICA)
                     }
                  >
                     Não se aplica
                  </SmallButton>
               )}
               {canAct && isNaoAplica && (
                  <SmallButton
                     type="button"
                     disabled={isBusy}
                     onClick={() =>
                        onSetStatus(doc, ADMISSION_DOCUMENT_STATUS.PENDENTE)
                     }
                  >
                     Reativar
                  </SmallButton>
               )}
            </div>
         </DocRowMain>

         {isExpanded && isValue && !isNaoAplica && (
            <DocRowDetail>
               <div className="detail-field">
                  <label htmlFor={`doc-value-${doc.id}`}>Valor</label>
                  <input
                     id={`doc-value-${doc.id}`}
                     type="text"
                     value={valueDraft}
                     onChange={(e) => setValueDraft(e.target.value)}
                     disabled={!canAct || isBusy}
                     placeholder="Ex.: nº da CTPS"
                     style={{
                        minHeight: 32,
                        padding: '6px 10px',
                        borderRadius: 8,
                        border: '1px solid rgba(0,0,0,0.15)',
                     }}
                  />
               </div>
               {canAct && (
                  <SmallButton
                     type="button"
                     $intent="write"
                     disabled={isBusy || !isDetailDirty}
                     onClick={handleSaveDetail}
                  >
                     Salvar
                  </SmallButton>
               )}
            </DocRowDetail>
         )}
      </DocRow>
   );
}

DocumentRow.propTypes = {
   document: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      status: PropTypes.string,
      value: PropTypes.string,
      has_file: PropTypes.bool,
      file_original_name: PropTypes.string,
      document_type: PropTypes.shape({
         code: PropTypes.string,
         name: PropTypes.string,
         collection_kind: PropTypes.string,
      }),
   }).isRequired,
   canAct: PropTypes.bool,
   isBusy: PropTypes.bool,
   onUploadFile: PropTypes.func.isRequired,
   onSaveDetail: PropTypes.func.isRequired,
   onSetStatus: PropTypes.func.isRequired,
   onDownload: PropTypes.func.isRequired,
};
